import { weekOffDays } from "../../../../utils/constants/enums";
import {
  OPEN_DAY_LABEL,
  SHIFT_STATUS_LABELS,
  TIMINGS_UNAVAILABLE_LABEL,
  WEEK_ORDER,
} from "../../utils/constants";
import { formatTimeRange, isOvernightRange } from "../../utils/formatters";

/**
 * The one place that knows the shift API's shape (GET /shifts, /shifts/:id, /shifts/options).
 *
 * A shift's `timings` is the v2 JSON: { version: 1, days: { monday: [{ start, end, auto, minHours? }] } }.
 * `auto: 1` = slots are generated; `auto: 0` = the mentor picks inside it ("flexible", with an
 * optional daily minimum); a missing or empty day is open; an end at or before its start ends the
 * next day.
 *
 * @typedef {Object} TimingGroupVM consecutive days with the same hours
 * @property {string} key
 * @property {string} dayRange     "Mon - Fri" / "Sat"
 * @property {string} label        "Mon–Fri (11:30 - 16:30)"
 * @property {string} timeLabel    "11:30 - 16:30", or "Open"
 * @property {boolean} isOpen
 * @property {boolean} isOvernight ends the next day ("+1")
 * @property {string|null} flexibleLabel "Flexible · min 5 h"; null when every chunk is generated
 *
 * @typedef {Object} ShiftVM
 * @property {number} id
 * @property {string} code
 * @property {object|null} timings     the raw v2 timings JSON, for the Edit form
 * @property {string} name
 * @property {string} codeLabel           "#UK-2"
 * @property {TimingGroupVM|{label: string, isOvernight: boolean}|null} timingSummary null = varies by day
 * @property {TimingGroupVM[]} timingGroups
 * @property {string|null} flexibleTag
 * @property {number} mentorCount         mapped mentors with an active login
 * @property {number} inactiveMentorCount mapped mentors whose login is off
 * @property {string} status
 * @property {string} statusLabel
 */

const shortDay = (day) => `${day?.charAt(0)?.toUpperCase()}${day?.slice(1, 3)}`;

const flexibleLabelOf = (chunks) => {
  const flexible = chunks?.filter((chunk) => chunk?.auto === 0);
  if (!flexible?.length) return null;
  const minHours = flexible.reduce((sum, chunk) => sum + (chunk?.minHours ?? 0), 0);
  const where =
    flexible.length === chunks.length
      ? ""
      : ` ${flexible.map((chunk) => formatTimeRange(chunk?.start, chunk?.end)).join(", ")}`;
  return `Flexible${where}${minHours ? ` · min ${minHours} h` : ""}`;
};

const toDayEntry = (day, chunks) => {
  if (!chunks?.length) {
    return { day, timeLabel: OPEN_DAY_LABEL, isOpen: true, isOvernight: false, flexibleLabel: null };
  }
  const first = chunks[0];
  const last = chunks[chunks.length - 1];
  return {
    day,
    timeLabel: formatTimeRange(first?.start, last?.end),
    isOpen: false,
    isOvernight: chunks.some((chunk) => isOvernightRange(chunk?.start, chunk?.end)),
    flexibleLabel: flexibleLabelOf(chunks),
  };
};

const sameHours = (a, b) =>
  a?.timeLabel === b?.timeLabel && a?.isOvernight === b?.isOvernight && a?.flexibleLabel === b?.flexibleLabel;

/** @param {object} timings the v2 timings JSON @returns {TimingGroupVM[]} Monday first */
export const toTimingGroups = (timings) =>
  WEEK_ORDER.map((day) => toDayEntry(day, timings?.days?.[day]))
    .reduce((groups, entry) => {
      const last = groups[groups.length - 1];
      if (last && sameHours(last, entry)) return [...groups.slice(0, -1), { ...last, lastDay: entry.day }];
      return [...groups, { ...entry, firstDay: entry.day, lastDay: entry.day }];
    }, [])
    .map(({ day, firstDay, lastDay, ...group }) => {
      const isSingleDay = firstDay === lastDay;
      const first = shortDay(firstDay);
      const last = shortDay(lastDay);
      return {
        key: `${firstDay}-${lastDay}`,
        dayRange: isSingleDay ? first : `${first} - ${last}`,
        label: `${isSingleDay ? first : `${first}–${last}`} (${group.timeLabel})`,
        ...group,
      };
    });

const flexibleTagOf = (timingGroups) => {
  const flexible = timingGroups.filter((group) => group.flexibleLabel);
  if (!flexible.length) return null;
  return timingGroups.length === 1 ? flexible[0].flexibleLabel : "Flexible";
};

/** @returns {ShiftVM} */
const toShiftVM = (shift) => {
  const hasTimings = !!shift?.timings;
  const timingGroups = hasTimings ? toTimingGroups(shift.timings) : [];
  let timingSummary = null;
  if (!hasTimings) timingSummary = { label: TIMINGS_UNAVAILABLE_LABEL, isOvernight: false };
  else if (timingGroups.length === 1) timingSummary = timingGroups[0];
  const mentorCount = shift?.activeMentors ?? 0;

  return {
    id: shift?.id,
    code: shift?.code ?? "",
    timings: shift?.timings ?? null,
    name: shift?.displayName ?? shift?.code ?? "",
    codeLabel: shift?.code ? `#${shift.code.replace(/_/g, "-")}` : "",
    timingSummary,
    timingGroups,
    flexibleTag: flexibleTagOf(timingGroups),
    mentorCount,
    inactiveMentorCount: Math.max((shift?.mappedMentors ?? 0) - mentorCount, 0),
    status: shift?.status,
    statusLabel: SHIFT_STATUS_LABELS?.[shift?.status] ?? shift?.status ?? "",
  };
};

/** GET /shifts → one row per shift, in the API's order (by id). @returns {ShiftVM[]} */
export const shiftMapper = (raw) => (raw?.shifts ?? []).map(toShiftVM);

/**
 * GET /shifts/:id → the mentors the popup lists: active logins only (in the API's order), and how
 * many inactive logins are also mapped to the shift.
 */
export const shiftMentorsMapper = (raw) => {
  const mentors = raw?.mentors ?? [];
  const active = mentors.filter((mentor) => mentor?.isActive);
  return {
    mentors: active.map((mentor) => ({
      id: mentor?.mentorId,
      name: mentor?.name || `Mentor #${mentor?.mentorId}`,
      idLabel: `#${mentor?.mentorId}`,
      weekOffLabel: weekOffDays?.[mentor?.weekOffDay]
        ? `Week off: ${weekOffDays[mentor.weekOffDay]}`
        : "No week off",
    })),
    inactiveCount: mentors.length - active.length,
  };
};

/** GET /shifts/options → the timings of one shift, or null when it is not offered (draft, disabled). */
export const optionTimingsFor = (raw, workShift) =>
  raw?.shifts?.find((shift) => shift?.id === Number(workShift))?.timings ?? null;

/**
 * The Edit mentor note: one line per group of days, e.g.
 * "Mon - Fri: 10:30 - 15:30 IST" and "Sat - Sun: 05:30 - 15:30 IST · Flexible · min 5 h".
 */
export const timingsNoteLabel = (timings) => {
  if (!timings) return null;
  return toTimingGroups(timings)
    .map((group) => {
      if (group.isOpen) return `${group.dayRange}: ${OPEN_DAY_LABEL}`;
      const hours = `${group.dayRange}: ${group.timeLabel} IST${group.isOvernight ? " (+1 day)" : ""}`;
      return group.flexibleLabel ? `${hours} · ${group.flexibleLabel}` : hours;
    })
    .join("\n");
};
